import {
  Kebab,
  Pencil,
  Copy,
  Link as LinkIcon,
  Trash,
  Folder,
  Download,
  LibraryIcon,
  Info,
  Lock,
} from '../../lib/icons.tsx';
import { useMenuKeys } from '../../lib/useMenuKeys.ts';
import { useDismissMenu } from '../../lib/useDismissMenu.ts';
import type { Dispatch, SetStateAction } from 'react';
import type { DeckSummary } from '../../../shared/deck.ts';

/** Everything a deck's kebab menu (and the card's zoom button) can do. Held as
 *  one object so a deck row takes one prop rather than nine. */
export type DeckCardActions = {
  onOpenOverview: (deck: DeckSummary) => void;
  onRename: (deck: DeckSummary) => void;
  onInfo: (deck: DeckSummary) => void;
  onCategories: (deck: DeckSummary) => void;
  onVisibility: (deck: DeckSummary) => void;
  onDuplicate: (deck: DeckSummary) => void;
  onSaveToLibrary: (deck: DeckSummary) => void;
  onExport: (deck: DeckSummary) => void;
  onCopyShareLink: (deck: DeckSummary) => void;
  onDelete: (deck: DeckSummary) => void;
};

type DeckMenuProps = {
  deck: DeckSummary;
  /** Identity of this menu within the page's single open-menu slot. */
  cardKey: string;
  menuOpen: boolean;
  busy: boolean;
  setMenuKey: Dispatch<SetStateAction<string | null>>;
  actions: DeckCardActions;
  /** The trigger's classes. The card wants `.deck-card__menu`, the rows and
   *  table views want their own placement, and the menu itself is identical in
   *  all three. */
  className?: string;
};

/**
 * A deck's kebab trigger and the menu it opens. Extracted from DeckCard on
 * 2026-09-02 when the rows and table views arrived: the eight verbs and their
 * exact wording are the deck's contract with the user, and three copies of it
 * would drift within a week.
 *
 * The open menu is page-singleton state (`setMenuKey`), not local, so opening
 * one closes any other, whichever view is on screen.
 *
 * Keyboard (2026-09-07): the menu takes focus when it opens, arrows walk it,
 * and Escape or a pick puts the caret back on the kebab. That behaviour is
 * `useMenuKeys`, shared with the Library's item menu since Run 11 — the two
 * menus' VERBS are their own pages' contracts, and only the keyboard is the
 * same thing twice.
 */
export default function DeckMenu({
  deck: d,
  cardKey,
  menuOpen,
  busy,
  setMenuKey,
  actions,
  className = 'iconbtn',
}: DeckMenuProps) {
  const { triggerId, btnRef, menuRef, onMenuKeyDown } = useMenuKeys(menuOpen, () => setMenuKey(null));
  // Click-away tests containment against the menu AND its kebab, so a
  // pointerdown on an item never unmounts the menu before the click lands.
  useDismissMenu([btnRef, menuRef], () => setMenuKey(null), menuOpen);

  return (
    <>
      <button
        ref={btnRef}
        id={triggerId}
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Actions for ${d.title}`}
        onClick={(e) => {
          e.stopPropagation();
          setMenuKey((p) => (p === cardKey ? null : cardKey));
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Kebab />
      </button>
      {menuOpen && (
        <div
          ref={menuRef}
          className="menu"
          role="menu"
          // Labelled BY the trigger rather than carrying its own copy of the deck
          // name: two labels reading "Actions for Deep Ocean" is the same fact
          // announced twice on the way in.
          aria-labelledby={triggerId}
          onKeyDown={onMenuKeyDown}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onRename(d);
            }}
            disabled={busy}
          >
            <Pencil /> <span>Rename</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onInfo(d);
            }}
          >
            <Info /> <span>Deck info…</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onCategories(d);
            }}
            disabled={busy}
          >
            <Folder /> <span>Categories…</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onVisibility(d);
            }}
            disabled={busy}
          >
            <Lock /> <span>Privacy…</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onDuplicate(d);
            }}
            disabled={busy}
          >
            <Copy /> <span>Duplicate</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onSaveToLibrary(d);
            }}
            disabled={busy}
          >
            <LibraryIcon /> <span>Save to library…</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenuKey(null);
              actions.onExport(d);
            }}
            disabled={busy}
          >
            <Download /> <span>Export (editable .zip)</span>
          </button>
          {d.publishedUrl && (
            <button
              type="button"
              role="menuitem"
              className="menu__item"
              onClick={() => {
                setMenuKey(null);
                actions.onCopyShareLink(d);
              }}
              disabled={busy}
            >
              <LinkIcon /> <span>Copy share link</span>
            </button>
          )}
          <hr className="menu__sep" />
          <button
            type="button"
            role="menuitem"
            className="menu__item menu__item--destructive"
            onClick={() => {
              setMenuKey(null);
              actions.onDelete(d);
            }}
            disabled={busy}
          >
            <Trash /> <span>Delete</span>
          </button>
        </div>
      )}
    </>
  );
}
